import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, XIcon, TruckIcon, ArrowRightIcon, CheckCircleIcon, PaperPlaneIcon, TrashIcon } from "@phosphor-icons/react";
import BarcodeScanInput from "@/components/BarcodeScanInput";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const ESTADO_CFG = {
  borrador: { l: "Borrador", color: "text-text-secondary border-border bg-app" },
  en_transito: { l: "En tránsito", color: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10" },
  recibida: { l: "Recibida", color: "text-agro-green border-agro-green/40 bg-agro-green/10" },
  anulada: { l: "Anulada", color: "text-red-400 border-red-400/40 bg-red-500/10" },
};

export default function WarehouseTransfers() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    bodega_origen_id: "", bodega_destino_id: "", observaciones: "",
    items: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: w }, { data: p }] = await Promise.all([
        api.get("/warehouse-transfers"),
        api.get("/warehouses", { params: { activo: true } }),
        api.get("/products"),
      ]);
      setItems(t); setWarehouses(w.filter((x) => x.tipo !== "transito")); setProducts(p);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { producto_id: "", producto_codigo: "", producto_nombre: "", cantidad: 1, ubicacion_destino: "" }] });
  // Escaneo con lector USB: agrega el producto (o suma 1 si ya está en la lista)
  const addScanned = (p) => {
    setForm((f) => {
      const idx = f.items.findIndex((it) => it.producto_id === p.id);
      if (idx >= 0) {
        const arr = [...f.items];
        arr[idx] = { ...arr[idx], cantidad: Number(arr[idx].cantidad || 0) + 1 };
        return { ...f, items: arr };
      }
      return { ...f, items: [...f.items, { producto_id: p.id, producto_codigo: p.codigo, producto_nombre: p.sku || p.nombre || p.categoria, cantidad: 1, ubicacion_destino: "" }] };
    });
    toast.success(`${p.codigo} agregado`);
  };
  const updItem = (i, f, v) => {
    const arr = [...form.items];
    arr[i][f] = v;
    if (f === "producto_id") {
      const p = products.find((x) => x.id === v);
      if (p) { arr[i].producto_codigo = p.codigo; arr[i].producto_nombre = p.sku || p.categoria; }
    }
    setForm({ ...form, items: arr });
  };
  const delItem = (i) => setForm({ ...form, items: form.items.filter((_, x) => x !== i) });

  const create = async (e) => {
    e.preventDefault();
    if (!form.bodega_origen_id || !form.bodega_destino_id) { toast.error("Selecciona origen y destino"); return; }
    if (form.bodega_origen_id === form.bodega_destino_id) { toast.error("Origen y destino no pueden ser iguales"); return; }
    if (form.items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    if (form.items.some((it) => !it.producto_id || Number(it.cantidad) <= 0)) { toast.error("Completa producto y cantidad de cada línea"); return; }
    try {
      await api.post("/warehouse-transfers", {
        bodega_origen_id: form.bodega_origen_id,
        bodega_destino_id: form.bodega_destino_id,
        observaciones: form.observaciones,
        items: form.items.map((it) => ({ ...it, cantidad: Number(it.cantidad) })),
      });
      toast.success("Transferencia creada en borrador");
      setOpen(false); setForm({ bodega_origen_id: "", bodega_destino_id: "", observaciones: "", items: [] });
      load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const dispatch = async (t) => {
    if (!window.confirm(`Despachar ${t.numero}? Se consumirá el stock de ${t.bodega_origen_codigo}.`)) return;
    try { await api.post(`/warehouse-transfers/${t.id}/dispatch`); toast.success("Despachado · en tránsito"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const receive = async (t) => {
    if (!window.confirm(`Recibir ${t.numero}? El stock entrará a ${t.bodega_destino_codigo}.`)) return;
    try { await api.post(`/warehouse-transfers/${t.id}/receive`); toast.success("Recibido"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const voidT = async (t) => {
    if (!window.confirm(`Anular ${t.numero}?`)) return;
    try { await api.post(`/warehouse-transfers/${t.id}/void`); toast.success("Anulado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const tm = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: create });
  return (
    <Layout
      title="Transferencias entre bodegas"
      subtitle="Mover stock entre bodegas conservando lote, costo PEPS y fecha de test"
      actions={
        <button data-testid="new-transfer-btn"
          onClick={() => setOpen(true)}
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95">
          <PlusIcon size={16} weight="bold" /> Nueva transferencia
        </button>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="transfers-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Número</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Origen → Destino</th>
                <th className="p-3 text-right">Items</th>
                <th className="p-3 text-right">Costo total</th>
                <th className="p-3">Estado</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-text-muted">
                  <TruckIcon size={32} className="inline mb-2 opacity-40" />
                  <div>No hay transferencias registradas</div>
                </td></tr>
              ) : items.map((t) => {
                const est = ESTADO_CFG[t.estado] || ESTADO_CFG.borrador;
                return (
                  <tr key={t.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`transfer-row-${t.numero}`}>
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{t.numero}</td>
                    <td className="p-3 text-xs text-text-secondary">{fmtDate(t.fecha)}</td>
                    <td className="p-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-text-primary">{t.bodega_origen_codigo}</span>
                        <ArrowRightIcon size={12} className="text-agro-yellow" />
                        <span className="font-mono text-text-primary">{t.bodega_destino_codigo}</span>
                      </div>
                      {t.observaciones && <div className="text-[10px] text-text-muted mt-1 truncate max-w-xs">{t.observaciones}</div>}
                    </td>
                    <td className="p-3 text-right text-sm">{(t.items || []).length}</td>
                    <td className="p-3 text-right text-sm font-medium">{fmtL(t.costo_total || 0)}</td>
                    <td className="p-3 text-xs">
                      <span className={`px-2 py-0.5 rounded border ${est.color}`}>{est.l}</span>
                    </td>
                    <td className="p-3 pr-5 text-right">
                      {t.estado === "borrador" && (
                        <>
                          <button onClick={() => dispatch(t)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Despachar" data-testid={`dispatch-${t.numero}`}><PaperPlaneIcon size={16} /></button>
                          <button onClick={() => voidT(t)} className="p-1.5 text-text-muted hover:text-red-400" title="Anular" data-testid={`void-transfer-${t.numero}`}><TrashIcon size={16} /></button>
                        </>
                      )}
                      {t.estado === "en_transito" && (
                        <>
                          <button onClick={() => receive(t)} className="p-1.5 text-text-muted hover:text-agro-green" title="Recibir" data-testid={`receive-${t.numero}`}><CheckCircleIcon size={16} /></button>
                          <button onClick={() => voidT(t)} className="p-1.5 text-text-muted hover:text-red-400" title="Anular" data-testid={`void-transfer-${t.numero}`}><TrashIcon size={16} /></button>
                        </>
                      )}
                      {t.estado === "recibida" && (
                        <button onClick={() => voidT(t)} className="p-1.5 text-text-muted hover:text-red-400" title="Anular" data-testid={`void-transfer-${t.numero}`}><TrashIcon size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...tm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
            <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold">Nueva transferencia</h3>
              <button aria-label="Cerrar" onClick={tm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
            </div>
            <form onSubmit={tm.guardar} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Bodega origen *</label>
                  <select required value={form.bodega_origen_id} onChange={(e) => setForm({ ...form, bodega_origen_id: e.target.value })}
                    data-testid="transfer-origen"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">—</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo} — {w.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Bodega destino *</label>
                  <select required value={form.bodega_destino_id} onChange={(e) => setForm({ ...form, bodega_destino_id: e.target.value })}
                    data-testid="transfer-destino"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">—</option>
                    {warehouses.filter((w) => w.id !== form.bodega_origen_id).map((w) => <option key={w.id} value={w.id}>{w.codigo} — {w.nombre}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium">Items *</label>
                  <button type="button" onClick={addItem} data-testid="transfer-add-item"
                    className="text-xs text-agro-green hover:text-agro-green-hover flex items-center gap-1">
                    <PlusIcon size={12} weight="bold" /> Agregar
                  </button>
                </div>
                <div className="mb-2"><BarcodeScanInput testid="transfer-barcode" onFound={addScanned} placeholder="Escanear producto (lector USB) y Enter para agregarlo…" /></div>
                {form.items.length === 0 ? (
                  <div className="text-xs text-text-muted text-center py-6 border border-dashed border-border rounded-md">
                    Sin items. Click “Agregar” para comenzar.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-border/60 rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                        <tr>
                          <th className="p-2 pl-3 text-left">Producto</th>
                          <th className="p-2 text-right">Cantidad</th>
                          <th className="p-2">Ubicación destino</th>
                          <th className="p-2 pr-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((it, i) => (
                          <tr key={i} className="border-t border-border/40">
                            <td className="p-2 pl-3">
                              <select value={it.producto_id} onChange={(e) => updItem(i, "producto_id", e.target.value)}
                                data-testid={`transfer-item-${i}-product`}
                                className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green">
                                <option value="">—</option>
                                {products.map((p) => <option key={p.id} value={p.id}>{p.codigo} — {p.sku} (stock: {fmtNum(p.stock_actual)})</option>)}
                              </select>
                            </td>
                            <td className="p-2">
                              <input type="number" min="0.0001" step="any" value={it.cantidad}
                                data-testid={`transfer-item-${i}-qty`}
                                onChange={(e) => updItem(i, "cantidad", e.target.value)}
                                className="w-24 bg-app border border-border rounded px-2 py-1.5 text-xs text-right focus:border-agro-green" />
                            </td>
                            <td className="p-2">
                              <input value={it.ubicacion_destino || ""} placeholder="P3-E2"
                                data-testid={`transfer-item-${i}-ubic`}
                                onChange={(e) => updItem(i, "ubicacion_destino", e.target.value)}
                                className="w-24 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green" />
                            </td>
                            <td className="p-2 pr-3 text-right">
                              <button aria-label="Eliminar" type="button" onClick={() => delItem(i)} className="text-text-muted hover:text-red-400">
                                <TrashIcon size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Observaciones</label>
                <textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  rows="2" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>

              <div className="text-xs text-text-muted bg-app border border-border rounded-md p-3 leading-relaxed">
                Al crear: la transferencia queda en <strong className="text-text-secondary">Borrador</strong>.
                Al despachar: el stock sale de la bodega origen (consumo PEPS conservando lotes/fecha de test).
                Al recibir: los lotes se crean en la bodega destino con el mismo costo y fechas.
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={tm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={tm.saving} type="submit" data-testid="transfer-submit"
                  className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">Crear transferencia</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
